import styled from 'styled-components';
import { hoverCapable } from '../../../../../styles/hover';

import {
  Tr as BaseTr,
  Th as BaseTh,
  Td as BaseTd,
} from '../../../../../components/display/tables/summaryTables/SummaryTablesStyles';
import { CheckBox } from '../../../../../components/inputs/CheckBox';

// ═══════════════════════════════════════════════════════════════════
// TABLE — feedback usuario 2026-07-07: "Tramos" pasa de tarjetas
// colapsables agrupadas por sentido a UNA tabla plana, siguiendo la
// arquitectura de tabla canónica de la app (cabecera gris + filas
// alineadas) en vez de un patrón visual propio.
//
// Reutiliza Container/Table/Thead/Tbody/Th/Td de
// `summaryTables/SummaryTablesStyles` — el MISMO stylesheet que compone
// `OperatorsSelectionTable` (crew tables) y las tablas de gestión
// (`TeamsManagementTable`, `VehiclesManagementTable`, etc.), citadas como
// referencia. Solo se añaden aquí las variantes locales de fila/celda que
// no existen en el stylesheet compartido.
// ═══════════════════════════════════════════════════════════════════

export {
  Container as SegmentsContainer,
  Table,
  Thead,
  Tbody,
  Th,
  Td,
} from '../../../../../components/display/tables/summaryTables/SummaryTablesStyles';

// El card padre (`SectionCard` en RouteDetailed/styles.ts) ya aporta el
// fondo `backgroundLila` + padding — aquí solo se resuelve el overflow-x
// para que la tabla no desborde la card en pantallas estrechas.
//
// Decisión usuario 2026-07-21: SIN paginación — la tabla crece libre hasta
// ~15 tramos y a partir de ahí scroll vertical. 655px = cabecera (40px) +
// 15 filas (41px medidas en vivo); con ≤15 tramos el contenido no llega al
// tope y no aparece scrollbar. El `Thead` compartido ya es sticky con
// fondo sólido, así que la cabecera se queda fija al hacer scroll.
export const TableScroll = styled.div`
  width: 100%;
  overflow-x: auto;
  max-height: 655px;
  overflow-y: auto;
`;

// Selección bidireccional tabla↔mapa (2026-07-08): `garnetClear` (10% de
// opacidad) apenas se distinguía del fondo `backgroundLila` de la card —
// se sube a `garnetLight` (20%, mismo token family, sin color nuevo) +
// un acento sólido en `garnet` (misma familia lila/primary — `primary`
// === `garnet` en ambos temas, ver theme/themes/*.ts) en el borde
// izquierdo de la PRIMERA celda — `border` en `tr` no se pinta en el
// modelo de tabla CSS (solo `background-color` tiene esa excepción en el
// spec), así que el acento va en el `td`, no en la fila.
export const SegmentRow = styled(BaseTr)<{
  $selected?: boolean;
  /** Tramo aún en la cola offline: se atenúa, igual que su línea en el mapa. */
  $pending?: boolean;
  /** «Así quedará»: un tramo de al lado, que no cambia. */
  $muted?: boolean;
  /** «Así quedará»: la parada nueva, entre las piezas. */
  $stop?: boolean;
  /**
   * La fila hace algo al pulsarla (29-oct): sin eso —«Así quedará» y las demás
   * tablas de los modales— ni mano ni resaltado al pasar el ratón, que tapaba
   * el lila de la parada.
   */
  $clickable?: boolean;
}>`
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  opacity: ${({ $pending }) => ($pending ? 0.65 : 1)};
  background-color: ${({ $selected, $muted, $stop, theme }) =>
    $selected
      ? theme.colors.garnetLight
      : $stop
        ? theme.colors.backgroundLila
        : $muted
          ? theme.colors.greyClear
          : 'transparent'};

  /* El Nº (primer span de la primera celda) también pierde el granate. */
  ${({ $muted, theme }) =>
    $muted &&
    `& > td, & > td button, & > td:first-of-type > span:first-child {
      color: ${theme.colors.greyDark};
    }`}

  & > td:first-of-type {
    border-left: 3px solid
      ${({ $selected, theme }) =>
        $selected ? theme.colors.garnet : 'transparent'};
  }

  /* Solo con ratón: en táctil el :hover se queda pegado tras el toque y no se
     distingue de la fila seleccionada (regla hover-tactil). */
  ${({ $clickable, $selected, theme }) =>
    $clickable
      ? `${hoverCapable} {
          &:hover {
            background-color: ${$selected ? theme.colors.garnetLight : theme.colors.greyClear};
          }
        }`
      : ''}
`;

export const SegmentNumber = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.garnet};
`;

export const DirectionCell = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

// Colores del icono de sentido: ascendente=verde, descendente=azul (mismos
// que el icono de cabecera de grupo del acordeón viejo) y "ambos" (↕)=neutro
// (`$ascending === null`). "Sin especificar" sigue SIN flecha (solo texto).
export const DirectionIcon = styled.span<{ $ascending: boolean | null }>`
  font-size: ${({ theme }) => theme.fontSize.S};
  color: ${({ $ascending, theme }) =>
    $ascending === null
      ? theme.colors.darkGrey
      : $ascending
        ? theme.colors.green
        : theme.colors.blue};
`;

// Causa de pausa — misma composición visual que el badge del acordeón
// viejo (icono + texto plano, sin color por causa): icono ⏸ + label.
export const CauseBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

export const CauseIcon = styled.span`
  font-size: ${({ theme }) => theme.fontSize.S};
`;

export const CauseLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.S};
  color: ${({ theme }) => theme.colors.black};
`;

// Tramo activo (sin endDate) — adición nueva (no existía en el acordeón
// viejo, que no distinguía visualmente el tramo en curso). Reutiliza el
// token de theme `routeStatus.active`, ya definido para este propósito
// exacto (leyenda de estado de segmentos en MapWithSegments) — ningún
// color nuevo.
export const ActiveBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: max-content;
  padding: 0.3rem 1.2rem;
  border-radius: 100rem;
  font-size: ${({ theme }) => theme.fontSize.XS};
  color: ${({ theme }) => theme.colors.black};
  background: ${({ theme }) => theme.colors.routeStatus.active};
`;

export const EmptyCellText = styled.span`
  color: ${({ theme }) => theme.colors.greyDark};
`;

// D3 (2026-07-23): link clicable a la Order vinculada (Act. básica / OT sala).
export const LinkText = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.garnet};
  font: inherit;
  text-decoration: underline;
  cursor: pointer;
  white-space: nowrap;

  /* Táctil: sin el guard el resaltado se queda pegado tras el toque. */
  ${hoverCapable} {
    &:hover {
      opacity: 0.8;
    }
  }
`;

// D2 (2026-07-23): celda de Descripción — trunca con ellipsis si es larga
// (la tabla es ancha; evita romper el layout de filas).
export const DescriptionCellText = styled.span`
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
`;

// P15 Fase 2 (2026-08-03): celda «Act. básica» de un tramo que cerró pidiendo
// Actuació y aún no la tiene. Se distingue del «-» (no la pidió nunca) sin
// gritar: es información, no una alerta — la acción para resolverlo está en la
// card de «Órdenes asociadas». Mismo `gold` que usa el aviso de tramo largo del
// cronómetro, que es el color de "esto pide atención, sin ser un error".
// Letra en `goldText` y no en `gold`: `gold` es el amarillo de las pills y como
// letra sobre la fila gris daba 1,23:1 — no se leía (feedback usuario 2026-10-26).
export const PendingCellText = styled.span`
  color: ${({ theme }) => theme.colors.goldText};
  white-space: nowrap;
`;

/**
 * Marca de "aún no sincronizado" junto al número de tramo. Va aquí y no inline
 * por la regla del proyecto: nada de `styled` dentro del `.tsx`.
 */
export const PendingMark = styled.span`
  margin-left: 0.4rem;
  font-size: ${({ theme }) => theme.fontSize.XS};
  line-height: 1;
  cursor: help;
`;

/** «Registrar parada»: la fila es una pieza de un tramo que gestión partió. */
export const SplitMark = styled(PendingMark)``;

/**
 * La marca ✎ / ↪ con su globo (30-oct, el usuario: «tiene que funcionar en
 * tablet»). El `title` del navegador solo sale con ratón; esto es el patrón de
 * `HelpTooltip`: un botón con el texto en `data-tooltip` que pinta el CSS, y
 * que también sale con `:focus`, porque al tocarlo en tablet coge el foco (al
 * tocar fuera se va). Sale a la DERECHA: hacia arriba lo taparía la cabecera
 * fija de la tabla en las primeras filas.
 */
export const MarkTip = styled.button`
  position: relative;
  margin-left: 0.4rem;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: ${({ theme }) => theme.fontSize.XS};
  line-height: 1;
  color: inherit;
  cursor: help;

  /* Área de toque cómoda en tablet sin descolocar la fila. */
  @media (hover: none) and (pointer: coarse) {
    min-width: 2.4rem;
    min-height: 2.4rem;
  }

  &:focus-visible {
    outline: 0.2rem solid ${({ theme }) => theme.colors.black};
    outline-offset: 0.2rem;
  }

  &::after {
    content: attr(data-tooltip);
    position: absolute;
    top: 50%;
    left: calc(100% + 0.8rem);
    transform: translateY(-50%);
    display: none;
    background: rgba(0, 0, 0, 0.88);
    color: white;
    padding: 0.6rem 1.1rem;
    border-radius: 0.5rem;
    font-size: 1.2rem;
    font-weight: 500;
    line-height: 1.45;
    width: max-content;
    max-width: 32rem;
    white-space: normal;
    text-align: left;
    z-index: ${({ theme }) => theme.zIndex.tooltip};
    pointer-events: none;
    box-shadow: 0 0.3rem 0.8rem rgba(0, 0, 0, 0.25);
  }

  &:focus::after {
    display: block;
  }

  ${hoverCapable} {
    &:hover::after {
      display: block;
    }
  }
`;

/** Varios enlaces en la celda de «Corregir» (Corregir · Parada · Deshacer). */
export const CellActions = styled.span`
  display: inline-flex;
  gap: 0.75rem;
  align-items: center;
`;

/**
 * Líneas de una misma celda una debajo de otra (la pill del aviso y lo que
 * cuelga de ella; el ámbar y su «Vincular»). Alineadas a la izquierda.
 */
export const CellStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.4rem;
`;

/**
 * ROID de la actuación/OT y la etiqueta de su estado SIEMPRE en la misma fila
 * (QA 3-nov): con `wrap` la celda se encogía a su mínimo, la etiqueta bajaba
 * de línea y esas filas salían más altas que las demás. La tabla ya tiene
 * scroll horizontal; mejor una columna algo más ancha que filas desiguales.
 */
export const LinkWithState = styled.div`
  display: inline-flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

/**
 * «Así quedará» de «Registrar parada» (29-oct): etiqueta junto al Nº que dice
 * qué cambia en la fila («Nuevo», «Era el Nº9»…). Letra oscura sobre fondo
 * claro (contraste ≥4,5:1). No es clicable: sin `:hover`.
 */
export const PreviewBadge = styled.span`
  display: inline-block;
  margin-left: 0.6rem;
  padding: 0.1rem 0.8rem;
  border-radius: 100rem;
  font-size: ${({ theme }) => theme.fontSize.XS};
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.garnet};
  background: ${({ theme }) => theme.colors.garnetLight};
`;

// ═══════════════════════════════════════════════════════════════════
// «Así quedará» en árbol (30-oct, el usuario: «no entiendo entre parada nueva
// y nuevo»). El tramo de hoy hace de padre; debajo cuelgan las piezas y, de
// cada pieza, la parada que la cierra. Solo se sangra la columna Nº, como en
// las tablas en árbol (las demás columnas siguen alineadas y cabe en tablet).
// ═══════════════════════════════════════════════════════════════════

/** Sangría por nivel del árbol. */
const TREE_INDENT = '2.2rem';
const TREE_LINE = '0.15rem';

/**
 * La celda del Nº: sangra según el nivel y hace de caja (`position: relative`)
 * para las líneas que la unen con su padre. Con `$depth` 0 queda como siempre.
 */
export const NumberTd = styled(BaseTd)<{ $depth: number }>`
  position: relative;

  && {
    padding-left: calc(
      ${({ theme }) => theme.spacing.l} + ${({ $depth }) => $depth} *
        ${TREE_INDENT}
    );
  }
`;

/**
 * Una línea del árbol: `pass` «│» (la de las piezas pasando por delante de una
 * parada), `tee` «├» y `elbow` «└». Va de borde a borde de la fila —un poco
 * más, para tapar la raya entre filas— y así las de filas seguidas se juntan.
 */
export const TreeGuide = styled.span<{
  $level: 1 | 2;
  $shape: 'pass' | 'tee' | 'elbow';
}>`
  position: absolute;
  top: -0.1rem;
  bottom: ${({ $shape }) => ($shape === 'elbow' ? '50%' : '-0.1rem')};
  left: calc(
    ${({ theme }) => theme.spacing.l} + ${({ $level }) => $level - 1} *
      ${TREE_INDENT} + 0.4rem
  );
  width: 1.2rem;
  border-left: ${TREE_LINE} solid ${({ theme }) => theme.colors.mediumGrey};
  border-bottom: ${({ $shape, theme }) =>
    $shape === 'elbow' ? `${TREE_LINE} solid ${theme.colors.mediumGrey}` : '0'};
  border-bottom-left-radius: ${({ $shape }) =>
    $shape === 'elbow' ? '0.4rem' : '0'};
  pointer-events: none;

  ${({ $shape, theme }) =>
    $shape === 'tee' &&
    `&::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      width: 1.2rem;
      border-top: ${TREE_LINE} solid ${theme.colors.mediumGrey};
    }`}
`;

/** «⏸ Parada» en la columna Nº de una fila de parada. */
export const StopLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: ${({ theme }) => theme.fontWeight.semiBold};
  color: ${({ theme }) => theme.colors.garnet};
`;

/**
 * Lo que dice la celda «Causa» de una parada pegada a un borde («Alarga la
 * pausa del Nº8…»). Parte línea: es una frase, no una etiqueta. Con ancho
 * mínimo: la tabla no cabe en el modal y encoge las columnas que pueden partir
 * línea hasta dejarla en tres.
 */
export const StopNote = styled.span`
  display: inline-block;
  min-width: 24rem;
  max-width: 32rem;
  white-space: normal;
  font-size: ${({ theme }) => theme.fontSize.S};
  color: ${({ theme }) => theme.colors.greyDark};
`;

// ═══════════════════════════════════════════════════════════════════
// Columna «Revisado» (spec 2026-10-27 § 9). Primera columna de la tabla.
// ═══════════════════════════════════════════════════════════════════

/**
 * Columna «Revisado» estrecha (`width: 1%` la ajusta a su contenido): solo la
 * casilla, como la columna «Importante» de las tablas (2-nov). Para qué sirve
 * lo explica la «i» junto al título «Tramos», que en tablet sale al tocarla.
 */
export const ReviewTh = styled(BaseTh)`
  width: 1%;
  padding-right: ${({ theme }) => theme.spacing.xs};
`;

export const ReviewTd = styled(BaseTd)`
  width: 1%;
  padding-right: ${({ theme }) => theme.spacing.xs};
`;

/**
 * La celda entera es la diana. En táctil mide ≥ 44 px de alto; con ratón la
 * fila queda como siempre (el mínimo solo aplica con `pointer: coarse`).
 */
export const ReviewTarget = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
  cursor: pointer;

  &:has([data-review-auto='true']) {
    cursor: default;
  }

  @media (pointer: coarse) {
    min-height: 4.4rem;
    min-width: 4.4rem;
  }
`;

export const ReviewHeader = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

/**
 * La casilla de siempre con fondo BLANCO cuando está vacía: sobre la cabecera
 * gris oscuro el borde negro y la raya de «a medias» no se veían. Marcada,
 * igual que siempre (relleno y borde de la casilla base). `&&` gana a los
 * estilos base del `CheckBox`, por eso el relleno marcado se repite aquí.
 */
export const ReviewCheckBox = styled(CheckBox)`
  && {
    background-color: ${({ theme }) => theme.colors.white};
  }
  &&:checked {
    background-color: ${({ theme }) => theme.colors.garnet};
  }
  /* Revisado solo: verde de «hecho» (no es una marca propia) y sin mano. */
  &&[data-review-auto='true']:checked {
    background-color: ${({ theme }) => theme.colors.darkForestGreen};
    border-color: ${({ theme }) => theme.colors.darkForestGreen};
    cursor: default;
  }
`;

/**
 * «Ha cambiado después»: solo el icono ⚠ en un círculo ámbar (`gold`, el de
 * «Parcial») con la letra en el tono OSCURO del ámbar (≥ 4,5:1). El texto va
 * en el `title` y para el lector de pantalla; con la pastilla de texto la
 * columna medía ~13rem.
 */
export const ChangedMark = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  font-size: ${({ theme }) => theme.fontSize.XS};
  line-height: 1;
  color: ${({ theme }) => theme.colors.tagTextGold};
  background: ${({ theme }) => theme.colors.gold};
`;

/** Texto solo para el lector de pantalla (no ocupa sitio). */
export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;
